import React, { useState } from 'react';
import {
  Users, UserPlus, UserMinus, ArrowLeftRight, UserX, KeyRound, Globe,
  Shield, CheckCircle, AlertTriangle, Activity, BarChart3, Clock, RefreshCw,
} from 'lucide-react';

interface BlueprintCard {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: string;
  category: string;
  rating: number;
  usageCount: number;
}

const AD_BLUEPRINTS: BlueprintCard[] = [
  { id: 'ad-joiner-lifecycle', name: 'Joiner – New Employee Onboarding', description: 'Create AD account, assign OU, set group memberships, provision mailbox, notify manager.', icon: UserPlus, difficulty: 'intermediate', duration: '~3 min/user', category: 'User Lifecycle', rating: 4.9, usageCount: 1247 },
  { id: 'ad-leaver-offboarding', name: 'Leaver – Employee Offboarding', description: 'Disable account, revoke groups, reset password, archive mailbox, revoke VPN/MFA.', icon: UserMinus, difficulty: 'intermediate', duration: '~2 min/user', category: 'User Lifecycle', rating: 4.8, usageCount: 987 },
  { id: 'ad-mover-transfer', name: 'Mover – Department Transfer', description: 'Update OU, recalculate group memberships based on new role, notify old and new managers.', icon: ArrowLeftRight, difficulty: 'beginner', duration: '~1 min/user', category: 'User Lifecycle', rating: 4.7, usageCount: 634 },
  { id: 'ad-inactive-user-cleanup', name: 'Inactive User Cleanup', description: 'Identify stale accounts, generate approval report, and disable upon approval.', icon: UserX, difficulty: 'beginner', duration: '~5 min (batch)', category: 'Security', rating: 4.8, usageCount: 892 },
  { id: 'ad-service-account-rotation', name: 'Service Account Password Rotation', description: 'Periodic password rotation with automatic update of dependent systems.', icon: KeyRound, difficulty: 'advanced', duration: '~10 min/account', category: 'Security', rating: 4.6, usageCount: 423 },
  { id: 'ad-dynamic-group-management', name: 'Dynamic Group Management', description: 'Sync AD group memberships based on user attributes – no manual intervention.', icon: Users, difficulty: 'intermediate', duration: '~5 min (sync)', category: 'Groups', rating: 4.7, usageCount: 756 },
  { id: 'ad-dns-record-automation', name: 'DNS A Record Lifecycle', description: 'Auto-create DNS A records on server provisioning, update on IP change, remove on decommission.', icon: Globe, difficulty: 'beginner', duration: '~30 sec', category: 'DNS', rating: 4.5, usageCount: 312 },
];

const STATS = [
  { label: 'Total Users', value: '4,821', icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { label: 'Active This Month', value: '3,412', icon: Activity, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { label: 'Accounts at Risk', value: '23', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { label: 'Automation Runs', value: '1,847', icon: RefreshCw, color: 'text-purple-400', bg: 'bg-purple-500/10' },
];

const DIFFICULTY_COLORS = {
  beginner: 'bg-emerald-500/20 text-emerald-300',
  intermediate: 'bg-blue-500/20 text-blue-300',
  advanced: 'bg-purple-500/20 text-purple-300',
};

export function ActiveDirectoryPage() {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const categories = ['All', 'User Lifecycle', 'Security', 'Groups', 'DNS'];

  const filtered = activeCategory === 'All'
    ? AD_BLUEPRINTS
    : AD_BLUEPRINTS.filter((b) => b.category === activeCategory);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Active Directory</h1>
          </div>
          <p className="text-gray-500 ml-13">User lifecycle, group management, DNS, and security automation</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-emerald-300 font-medium">Connected</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          Recent Automation Activity
        </h3>
        <div className="space-y-2">
          {[
            { action: 'Joiner: John Smith onboarded', time: '2 min ago', status: 'success', detail: 'Account created → Groups assigned → Manager notified' },
            { action: 'Inactive cleanup: 12 accounts disabled', time: '1 hour ago', status: 'success', detail: 'Monthly cleanup run completed' },
            { action: 'Leaver: Sarah Jones offboarded', time: '3 hours ago', status: 'success', detail: 'Account disabled → Groups removed → Mailbox archived' },
            { action: 'Service Account Rotation: svc-sql01', time: '6 hours ago', status: 'warning', detail: 'Password rotated — Awaiting dependent system update' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-200 last:border-0">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === 'success' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium">{item.action}</div>
                <div className="text-xs text-gray-400">{item.detail}</div>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">{item.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Blueprints */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Automation Blueprints</h2>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((bp) => {
            const Icon = bp.icon;
            return (
              <div key={bp.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orbayx-500/30 transition-all group cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_COLORS[bp.difficulty]}`}>
                    {bp.difficulty}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-orbayx-700 transition-colors">{bp.name}</h3>
                <p className="text-xs text-gray-400 mb-3 line-clamp-2">{bp.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {bp.duration}
                  </span>
                  <span>⭐ {bp.rating} · {bp.usageCount.toLocaleString()} runs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
